import { ItemPriceBadge } from "@/components/item/ItemPriceBadge";
import { SuitableDietsGrid } from "@/components/item/SuitableDietsGrid";
import { Button, Divider, Heading, Icon, PageContainer, Surface, Text } from "@/components/ui";
import { CaretDown } from "@/icons";
import { cn } from "@/lib/cn";
import type { ItemOffer } from "@/types";
import { useState, type ReactNode } from "react";

const DESCRIPTION_FOLD_LENGTH = 140;

type ItemTitleBlockProps = {
  name: string;
  description?: string;
  itemOffer: ItemOffer;
  minimumOfferPrice: number | null;
  suitableDiets?: string[];
  children?: ReactNode;
};

/** Card panel under the photo: price badge, the item's h1, a "Ver mais" description and diets; `children` sit below on the same surface. */
export function ItemTitleBlock({
  name,
  description,
  itemOffer,
  minimumOfferPrice,
  suitableDiets,
  children,
}: ItemTitleBlockProps) {
  const [expanded, setExpanded] = useState(false);
  const canExpand = Boolean(description) && ((suitableDiets?.length ?? 0) > 0 || (description?.length ?? 0) > DESCRIPTION_FOLD_LENGTH);

  return (
    <Surface surface="card" className="z-raised shadow-sticky">
      <Divider tone="subtle" />
      <PageContainer surface="card">
        <div className="-mt-14 flex items-end justify-end">
          <ItemPriceBadge itemOffer={itemOffer} minimumOfferPrice={minimumOfferPrice} />
        </div>

        <div className="mb-2 flex flex-col gap-2">
          <Heading level={1}>{name}</Heading>
          <div>
            <Text tone="muted" clamp={expanded ? undefined : 3}>
              {description}
            </Text>
            {expanded && <SuitableDietsGrid suitableDiets={suitableDiets} />}
            {canExpand && (
              <div className="mt-2 flex justify-center">
                <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} className="gap-1">
                  {expanded ? "Ver menos" : "Ver mais"}
                  <Icon
                    as={CaretDown}
                    size="sm"
                    className={cn("transition-transform duration-base ease-standard", expanded && "rotate-180")}
                  />
                </Button>
              </div>
            )}
          </div>
        </div>
      </PageContainer>
      {children}
    </Surface>
  );
}
